/*
 * Who may do what: the one permission table of the app. The UI hides or disables what a role cannot do
 * (with a reason), the mock API enforces the same table (`route.post(path, fn, { can: 'servers.manage' })`
 * answers 403 `forbidden`), and /team shows it as the permission matrix.
 *
 *   import { can } from '@/lib/permissions'
 *   can(role, 'billing.manage')
 *   const allowed = useCan(); allowed('servers.cancel')   // from '@/api/hooks/team', for the signed-in member
 */
import type { Role } from '@/api/types/team'

export type Action =
  /** see servers, their status, telemetry and activity */
  | 'servers.view'
  /** add, install, reveal or reissue keys, transfer, update channel, rename, approve a device code */
  | 'servers.manage'
  /** attach a server's diagnostics to a support ticket */
  | 'diagnostics.share'
  /** open and reply to support tickets */
  | 'support.use'
  /** read the release notes */
  | 'releases.view'
  /** change plan or cycle, payment methods, pay invoices, billing details */
  | 'billing.manage'
  /** buy AI credit packs, auto top-up and AI settings */
  | 'credits.manage'
  /** cancel or resume a server's subscription, remove a server that never activated */
  | 'servers.cancel'
  /** invite members, change roles, remove members */
  | 'team.manage'
  /** organisation details, logo and security policy */
  | 'org.manage'
  /** transfer ownership, delete the organisation */
  | 'org.owner'

const ALL: readonly Role[] = ['owner', 'admin', 'billing', 'technical']
const TECHNICAL: readonly Role[] = ['owner', 'admin', 'technical']
const BILLING: readonly Role[] = ['owner', 'admin', 'billing']
const ADMINS: readonly Role[] = ['owner', 'admin']
const OWNER: readonly Role[] = ['owner']

export const PERMISSIONS: Record<Action, readonly Role[]> = {
  'servers.view': ALL,
  'support.use': ALL,
  'releases.view': ALL,
  'servers.manage': TECHNICAL,
  'diagnostics.share': TECHNICAL,
  'billing.manage': BILLING,
  'credits.manage': BILLING,
  'servers.cancel': ADMINS,
  'team.manage': ADMINS,
  'org.manage': ADMINS,
  'org.owner': OWNER,
}

/** Every action, in the order the permission matrix lists them. */
export const ACTIONS = Object.keys(PERMISSIONS) as Action[]

export function can(role: Role | null | undefined, action: Action): boolean {
  return !!role && PERMISSIONS[action].includes(role)
}

/** The roles that may take an action, lowest first, for "ask an owner or admin" style reasons. */
export function rolesFor(action: Action): readonly Role[] {
  return PERMISSIONS[action]
}
